import React, { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, RadioGroup, FormControlLabel, Radio, LinearProgress
} from '@mui/material';
import { MdPersonAddDisabled } from "react-icons/md";
import { BsFillPersonCheckFill } from "react-icons/bs";
import Swal from 'sweetalert2';

import {
  obtenerVoluntariosPaginados,
  registrarVoluntario,
  habilitarPersonal,
  inhabilitarPersonal
} from '../../../services/PersonalBibliotecarioService';

import { esCorreoValido } from '../../../utils/functions';
import PasswordFieldWithPolicy from '../passwordFieldWithPolicy/PasswordFieldWithPolicy'; // <<--- AJUSTA LA RUTA SI ES NECESARIO
import { passwordPolicyCheck } from '../../../utils/passwordPolicy';       // <<--- AJUSTA LA RUTA SI ES NECESARIO
import JumpByItemFooter from '../customTableFooter/JumpByItemFooter';
import './voluntarios.css';
import { useUser } from '../../../context/userContext';
import HorasVoluntariado, { formatearHoras } from './horasVoluntariado';

function Voluntarios() {
  const { userData } = useUser();
  const puedeVerHoras = userData?.authRole === 'admin' || userData?.authRole === 'biblioteca';
  const [voluntarioHoras, setVoluntarioHoras] = useState(null);
  const [voluntarios, setVoluntarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingRegistro, setLoadingRegistro] = useState(false);
  const [pageVoluntarios, setPageVoluntarios] = useState(0);
  const [pageSizeVoluntarios] = useState(10);
  const [pagingMetadata, setPagingMetadata] = useState({});
  const [openDialog, setOpenDialog] = useState(false);

  const [confirmPassword, setConfirmPassword] = useState('');
  const [errores, setErrores] = useState({
    nombreCompleto: '',
    correoInstitucional: '',
    password: ''
  });

  const [nuevoVoluntario, setNuevoVoluntario] = useState({
    nombreCompleto: '',
    correoInstitucional: '',
    password: '',
    sexo: 'M',
  });

  const fetchVoluntarios = async () => {
    setLoading(true);
    try {
      const response = await obtenerVoluntariosPaginados(pageVoluntarios, pageSizeVoluntarios);
      const { content, totalItems, totalPages, totalElements } = response.data;
      setVoluntarios(content);
      const totalElementos = totalItems || totalElements;
      setPagingMetadata({ totalElements: totalElementos });
    } catch (error) {
      console.error('Error al obtener voluntarios', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVoluntarios();
  }, [pageVoluntarios]);

  const handleOpenDialog = () => setOpenDialog(true);

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNuevoVoluntario({
      nombreCompleto: '',
      correoInstitucional: '',
      password: '',
      sexo: 'M',
    });
    setConfirmPassword('');
    setErrores({ nombreCompleto: '', correoInstitucional: '', password: '' });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNuevoVoluntario(prev => ({ ...prev, [name]: value }));

    // Limpia errores en tiempo real
    if (name in errores && errores[name]) {
      setErrores(prev => ({ ...prev, [name]: '' }));
    }
    if (name === 'password' && errores.password) {
      setErrores(prev => ({ ...prev, password: '' }));
    }
  };

  const handleRegistrarVoluntario = async () => {
    const { nombreCompleto, correoInstitucional, password } = nuevoVoluntario;
    let hayErrores = false;
    const nuevosErrores = { nombreCompleto: '', correoInstitucional: '', password: '' };

    // Validación de campos
    if (!nombreCompleto) {
      nuevosErrores.nombreCompleto = 'Este campo es requerido';
      hayErrores = true;
    }
    if (!correoInstitucional) {
      nuevosErrores.correoInstitucional = 'Este campo es requerido';
      hayErrores = true;
    } else if (!esCorreoValido(correoInstitucional)) {
      nuevosErrores.correoInstitucional = 'Ingresa un correo electrónico válido';
      hayErrores = true;
    }

    // Política de contraseña
    if (!password) {
      nuevosErrores.password = 'Este campo es requerido';
      hayErrores = true;
    } else {
      const { valid } = passwordPolicyCheck(password, { nombreCompleto, correoInstitucional });
      if (!valid) {
        nuevosErrores.password = 'La contraseña no cumple con la política de seguridad.';
        hayErrores = true;
      }
    }

    // Confirmación de contraseña
    if (!confirmPassword) {
      nuevosErrores.password = nuevosErrores.password || 'Confirma tu contraseña.';
      hayErrores = true;
    } else if (password && confirmPassword && password !== confirmPassword) {
      nuevosErrores.password = 'Las contraseñas no coinciden.';
      hayErrores = true;
    }

    setErrores(nuevosErrores);
    if (hayErrores) return;

    setLoadingRegistro(true);
    try {
      await registrarVoluntario(nuevoVoluntario);
      handleCloseDialog();
      Swal.fire({
        title: 'Éxito!',
        text: 'El voluntario ha sido registrado con éxito.',
        icon: 'success',
        confirmButtonText: 'Ok',
        confirmButtonColor: '#21807C',
        customClass: { container: 'swal-overlay' },
      });
      fetchVoluntarios();
    } catch (error) {
      const mensajeError =
        error?.response?.data && typeof error.response.data === 'object'
          ? error.response.data.message
          : error?.response?.data || 'No se pudo registrar el voluntario.';
      handleCloseDialog();
      Swal.fire({
        title: 'Error!',
        text: mensajeError,
        icon: 'error',
        confirmButtonText: 'Ok',
        confirmButtonColor: '#21807C',
        customClass: { container: 'swal-overlay' },
      });
    } finally {
      setLoadingRegistro(false);
    }
  };

  const handleCambiarEstadoVoluntario = async (id, habilitar) => {
    try {
      const action = habilitar ? habilitarPersonal : inhabilitarPersonal;
      await action(id);
      Swal.fire({
        title: 'Éxito!',
        text: `El voluntario ha sido ${habilitar ? 'habilitado' : 'inhabilitado'} con éxito.`,
        icon: 'success',
        confirmButtonText: 'Ok',
        confirmButtonColor: '#21807C',
        customClass: { container: 'swal-overlay' },
      });
      fetchVoluntarios();
    } catch (error) {
      Swal.fire({
        title: 'Error!',
        text: `No se pudo ${habilitar ? 'habilitar' : 'inhabilitar'} al voluntario.`,
        icon: 'error',
        confirmButtonText: 'Ok',
        confirmButtonColor: '#21807C',
        customClass: { container: 'swal-overlay' },
      });
    }
  };

  const columnas = [
    ...(puedeVerHoras ? [
      {
        field: 'horas', headerName: 'Horas acumuladas', width: 170, sortable: false,
        headerClassName: 'tablaVoluntarios-headers', align: 'center', headerAlign: 'center',
        renderCell: (params) => formatearHoras(params.value?.totalSegundos)
      },
      {
        field: 'historialHoras', headerName: 'Historial de horas', width: 170, sortable: false,
        headerClassName: 'tablaVoluntarios-headers', align: 'center', headerAlign: 'center',
        renderCell: (params) => <Button color="success" onClick={() => setVoluntarioHoras(params.row)}>Ver detalles</Button>
      }
    ] : []),
    { field: 'id', headerName: 'ID', width: 100, headerClassName: 'tablaVoluntarios-headers', align: 'center', headerAlign: 'center' },
    {
      field: 'userPic',
      headerName: 'Foto de Perfil',
      sortable: false,
      width: 150,
      align: 'center',
      headerAlign: 'center',
      headerClassName: 'tablaVoluntarios-headers',
      renderCell: (params) => (
        <img src={params.value} alt="Foto de Perfil" style={{ width: '71px', height: '71px', borderRadius: '50%', objectFit: 'cover' }} />
      )
    },
    { field: 'nombreCompleto', headerName: 'Nombre', width: 250, headerClassName: 'tablaVoluntarios-headers', align: 'center', headerAlign: 'center' },
    { field: 'correoInstitucional', headerName: 'Correo Institucional', width: 250, headerClassName: 'tablaVoluntarios-headers', align: 'center', headerAlign: 'center' },
    {
      field: 'estado',
      headerName: 'Estado',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      headerClassName: 'tablaVoluntarios-headers',
      renderCell: (params) => {
        return (
          <span
            style={{
              backgroundColor: params.row.estado ? 'rgb(145 254 159 / 80%)' : '#ffadad8f',
              color: params.row.estado ? 'green' : 'red',
              padding: '6px',
              borderRadius: '4px'
            }}
          >
            {params.row.estado ? 'Habilitado' : 'Deshabilitado'}
          </span>
        );
      }
    },
    {
      field: 'acciones',
      headerName: 'Habilitar / Deshabilitar',
      sortable: false,
      width: 150,
      headerAlign: 'center',
      align: 'center',
      headerClassName: 'tablaVoluntarios-headers',
      renderCell: (params) => (
        <Button
          className={params.row.estado ? 'tableInventario-deleteBtn' : 'tableVoluntarios-habilitarBtn'}
          style={{ width: '40px', height: '40px' }}
          onClick={() => handleCambiarEstadoVoluntario(params.row.id, !params.row.estado)}
        >
          {params.row.estado ? <MdPersonAddDisabled color='white' size={22} /> : <BsFillPersonCheckFill color='white' size={22} />}
        </Button>
      ),
    },
  ];

  return (
    <div className='voluntarios-container'>
      <h1>Voluntarios</h1>
      {puedeVerHoras && voluntarioHoras ? (
        <HorasVoluntariado
          key={voluntarioHoras.id} voluntario={voluntarioHoras}
          onClose={() => setVoluntarioHoras(null)} onUpdated={fetchVoluntarios}
        />
      ) : null}
      <div className='voluntarios-top'>
        <button onClick={() => setOpenDialog(true)}>Registrar</button>
      </div>

      {/* Modal para registrar voluntario */}
      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>Registrar Voluntario</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="nombreCompleto"
            label="Nombre Completo"
            type="text"
            fullWidth
            variant="outlined"
            value={nuevoVoluntario.nombreCompleto}
            onChange={handleChange}
            error={!!errores.nombreCompleto}
            helperText={errores.nombreCompleto}
          />
          <TextField
            margin="dense"
            name="correoInstitucional"
            label="Correo Institucional"
            type="email"
            fullWidth
            variant="outlined"
            value={nuevoVoluntario.correoInstitucional}
            onChange={handleChange}
            error={!!errores.correoInstitucional}
            helperText={errores.correoInstitucional}
          />

          {/* Campo reutilizable de contraseña + checklist + confirmar (con íconos a color) */}
          <PasswordFieldWithPolicy
            value={nuevoVoluntario.password}
            onChange={handleChange}
            name="password"
            label="Contraseña"
            fullName={nuevoVoluntario.nombreCompleto}
            email={nuevoVoluntario.correoInstitucional}
            errorText={errores.password}
            confirmValue={confirmPassword}
            onConfirmChange={(e) => {
              setConfirmPassword(e.target.value);
              if (errores.password) setErrores(prev => ({ ...prev, password: '' }));
            }}
            showConfirm
          />

          <RadioGroup
            row
            name="sexo"
            value={nuevoVoluntario.sexo}
            onChange={handleChange}
          >
            <FormControlLabel value="M" control={<Radio />} label="Masculino" />
            <FormControlLabel value="F" control={<Radio />} label="Femenino" />
          </RadioGroup>

          {loadingRegistro && <LinearProgress color="success" />}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRegistrarVoluntario} color="success" disabled={loadingRegistro}>
            Registrar
          </Button>
          <Button onClick={handleCloseDialog} color="error" disabled={loadingRegistro}>
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>

      <div className='tablaVoluntariosContainer'>
        <DataGrid
          rows={voluntarios}
          columns={columnas}
          paginationMode="server"
          pagination
          paginationModel={{ page: pageVoluntarios, pageSize: pageSizeVoluntarios }}
          onPaginationModelChange={(model) => setPageVoluntarios(model.page)}
          rowCount={pagingMetadata.totalElements}
          loading={loading}
          getRowId={(row) => row.id}
          disableSelectionOnClick
          rowHeight={80}
          hideFooterSelectedRowCount
          pageSizeOptions={[]}
          slots={{ footer: JumpByItemFooter }}
          slotProps={{
            footer: {
              total: pagingMetadata.totalElements ?? 0,
              page: pageVoluntarios,
              pageSize: pageSizeVoluntarios,
              onJumpPage: (p) => setPageVoluntarios(p),
              noun: "voluntario",
            },
          }}
        />
      </div>
    </div>
  );
}

export default Voluntarios;
